import React, { useEffect, useRef, useState } from "react"
import { Select } from "./Elements/Select"
import styled from "styled-components"
import "./Pagination.css"
import { useTranslation } from "react-i18next"
import { Vl } from "../../../../../ProjectNonSpecificComponents/Elements/Vl"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import { TInputEvent } from "../../../../Interfaces/GlobalInterface"
import { TChangeNumber } from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Icon from 'components/ProjectSpecificComponents/Elements/Global/atoms/Icons/Icon/Icon'

interface Props {
	currentPage: number
	rowsPerPage: number
	changeRowsPerPage: TChangeNumber
	totalRowCount: number
	paginate: TChangeNumber
	pageCount: number
}

export default function Pagination({
	currentPage,
	rowsPerPage,
	changeRowsPerPage,
	totalRowCount,
	paginate,
	pageCount,
}: Props) {
	const pageNumbers = []
	const { t } = useTranslation()
	const { isMobile, isDesktop } = useScreenType()

	useEffect(() => {
		setItemsFrom(calcItemsFrom())
		setItemsTo(calcItemsTo())
	}, [rowsPerPage, currentPage])

	const calcItemsFrom = () => Number(rowsPerPage) * (Number(currentPage) - 1) + 1

	const calcItemsTo = () => Number(rowsPerPage) * (Number(currentPage) - 1) + Number(rowsPerPage)

	const [itemsFrom, setItemsFrom] = useState<number>(0)
	const [itemsTo, setItemsTo] = useState<number>(0)
	// State used for page visualization without triggering pagination
	const [selectedPageNr, setSelectedPageNr] = useState<number | string>(currentPage)
	const [pageOutOfBounds, setPageOutOfBounds] = useState<boolean>(false)
	for (let i = 1; i <= pageCount; i++) {
		pageNumbers.push(i)
	}

	const paginateRef = useRef<any>(null)

	const inputPage = (e: TInputEvent) => {
		setPageOutOfBounds(false)
		const pageNr: number = Number(e.target.value)
		setSelectedPageNr(e.target.value)

		switch (true) {
			case pageNr <= 0:
				return
			case pageNr <= pageCount:
				return paginate(pageNr)
			default:
				return setPageOutOfBounds(true)
		}
	}

	const goToFirstPage = () => {
		paginate(1)
		setSelectedPageNr(1)
	}

	const goToPrevPage = () => {
		if (currentPage <= 1) return goToFirstPage()
		paginate(currentPage - 1)
		setSelectedPageNr((selectedPage) => Number(selectedPage) - 1)
	}

	const goToNextPage = () => {
		if (currentPage >= pageCount) return goToLastPage()
		paginate(currentPage + 1)
		setSelectedPageNr((selectedPage) => Number(selectedPage) + 1)
	}

	const goToLastPage = () => {
		paginate(pageCount)
		setSelectedPageNr(pageCount)
	}

	const SelectCustomStyle: React.CSSProperties = {
		maxWidth: 50,
		margin: "0px 10px",
		textAlign: "center",
		fontSize: "inherit",
		height: 30,
	}

	const toggleRowsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
		goToFirstPage()
		changeRowsPerPage(Number(e.target.value))
	}

	/**
	 * Read paginations width when table gets opened.
	 * If HD is open on fullscreen, but in smaller window, some elements will be hidden
	 */

	const getPaginationElWidth = () =>
		document?.getElementById("pagination-layout")?.offsetWidth ?? window.screen.width

	return (
		<PaginatinLayout id="pagination-layout">
			<HyperDiv direction="row" centered style={{ gridArea: "paginate", fontSize: 12 }}>
				{getPaginationElWidth() > 600 && !isMobile && (
					<HyperDiv onClick={goToFirstPage} centered className="pointer">
						<Icon iconKey="arrowLeftEnd" height={14} />
					</HyperDiv>
				)}
				<HyperDiv onClick={goToPrevPage} centered className="pointer">
					<Icon iconKey="arrowLeft" height={14} />
				</HyperDiv>
				<Vl />
				{!isMobile && <div>{t("table.footer.page")}</div>}
				<Input
					value={selectedPageNr}
					onChange={inputPage}
					type="number"
					style={{ ...SelectCustomStyle }}
				/>
				{t("table.footer.of")}
				<HyperDiv style={{ paddingLeft: 10 }}>{pageCount}</HyperDiv>
				<Vl />
				<HyperDiv onClick={goToNextPage} centered className="pointer">
					<Icon iconKey="arrowRight" height={14} />
				</HyperDiv>
				{getPaginationElWidth() > 600 && !isMobile && (
					<HyperDiv onClick={goToLastPage} centered className="pointer">
						<Icon iconKey="arrowRightEnd" height={14} />
					</HyperDiv>
				)}
			</HyperDiv>

			{!isMobile && getPaginationElWidth() > 800 && (
				<HyperDiv
					direction="row"
					style={{ float: "right", gridArea: "info", fontSize: 12 }}
					centeredY
					alignedRight
				>
					<HyperDiv style={{ fontSize: "inherit" }} className="hide-overflown-text-nowrap">
						{isDesktop && t("table.footer.itemCount")} {`${itemsFrom}-${itemsTo}`}{" "}
						{t("table.footer.of")} {totalRowCount}
					</HyperDiv>
					<Vl />
					{isDesktop && (
						<HyperDiv className="hide-overflown-text-nowrap">
							{t("table.footer.itemsPerPage")}
						</HyperDiv>
					)}
					<Select
						placeholder="Ierakstu skaits lapā"
						onChange={(e) => toggleRowsPerPage(e)}
						defaultValue={rowsPerPage}
						style={{ ...SelectCustomStyle }}
					>
						<option value={5}>5</option>
						<option value={10}>10</option>
						<option value={15}>15</option>
						<option value={25}>25</option>
						<option value={50}>50</option>
					</Select>
				</HyperDiv>
			)}
		</PaginatinLayout>
	)
}

const PaginatinLayout = styled.div`
	border-top: 5px solid var(--light-light-gray);
	padding-top: 10px;

	width: calc(100%);
	display: grid;
	grid-template-columns: 33% 34% 33%;

	grid-template-areas: "blank paginate info";
`
